import { useState, useEffect, useCallback } from "react";
import { Button, Table, Select, Input, Loader, Pagination } from "semantic-ui-react";
import { toast } from "../..";
import "./ParameterOverviewTable.css";
import {
    displayNetworkCodeOption,
    initParameterFilter,
    IParameter,
    IParameterFilter,
    NetworkCode,
    networkCodeOptions,
} from "../../models/parameter";
import { getParameters } from "../../services/parametersService";
import { yearsProps } from "../../models/common";
import { useHistory } from "react-router-dom";
import { timeDateFormat, localeString } from "../../helpers/commonHelper";

const NumberOfItemsToFetch = 5;

export const ParameterOverviewTable = () => {
    const history = useHistory();

    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [parameterFilter, setParameterFilter] = useState<IParameterFilter>(initParameterFilter);
    const [tableData, setTableData] = useState<IParameter[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchParameters = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getParameters(parameterFilter, page, NumberOfItemsToFetch, false, false);
            setTableData(res.result);
            setTotalRows(res.totalRows);
        } catch {
            toast("Error fetching parameters", false, 1000);
        }
        setLoading(false);
    }, [parameterFilter, page]);

    useEffect(() => {
        fetchParameters();
    }, [fetchParameters]);

    useEffect(() => {
        setPage(1);
    }, [parameterFilter]);

    const openForm = (element: IParameter) => {
        history.push(`/parameters/view/${element.guid}/answer/${element.answerGuid ?? ""}`);
    };

    return (
        <>
            <Table fixed className="forms-overview">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>
                            <Input
                                fluid
                                value={parameterFilter.name}
                                onChange={e =>
                                    setParameterFilter({
                                        ...parameterFilter,
                                        name: e.target.value,
                                    })
                                }
                                icon="search"
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Select
                                clearable
                                selectOnBlur={false}
                                className="filter-selection"
                                options={networkCodeOptions}
                                value={parameterFilter.networkCode}
                                onChange={(_e, data) =>
                                    setParameterFilter({
                                        ...parameterFilter,
                                        networkCode:
                                            data.value === ""
                                                ? undefined
                                                : (data.value as NetworkCode),
                                    })
                                }
                                text={
                                    parameterFilter.networkCode !== undefined
                                        ? displayNetworkCodeOption(parameterFilter.networkCode)
                                        : "Network code"
                                }
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Select
                                clearable
                                selectOnBlur={false}
                                className="filter-selection"
                                options={yearsProps}
                                value={parameterFilter.year}
                                onChange={(_e, data) =>
                                    setParameterFilter({
                                        ...parameterFilter,
                                        year: data.value as string,
                                    })
                                }
                                text={parameterFilter.year ? parameterFilter.year : "Year"}
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Button
                                primary
                                content="Clear filters"
                                onClick={() => {
                                    setParameterFilter(initParameterFilter);
                                }}
                            />
                        </Table.HeaderCell>
                    </Table.Row>
                    <Table.Row>
                        <Table.HeaderCell>Parameter</Table.HeaderCell>
                        <Table.HeaderCell>Network code</Table.HeaderCell>
                        <Table.HeaderCell>Year</Table.HeaderCell>
                        <Table.HeaderCell>Deadline</Table.HeaderCell>
                        <Table.HeaderCell>Time of submission</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {tableData?.map(element => {
                        return (
                            <Table.Row key={element.guid}>
                                <Table.Cell
                                    className={`clickable ${element.status}`}
                                    onClick={openForm.bind(null, element)}
                                >
                                    {element.name}
                                </Table.Cell>
                                <Table.Cell>
                                    {displayNetworkCodeOption(element.networkCode)}
                                </Table.Cell>
                                <Table.Cell>{element.year}</Table.Cell>
                                <Table.Cell>
                                    {element.deadline
                                        ? new Date(element.deadline).toLocaleString(
                                              localeString,
                                              timeDateFormat
                                          )
                                        : ""}
                                </Table.Cell>
                                <Table.Cell>
                                    {element.timeSubmitted
                                        ? new Date(element.timeSubmitted).toLocaleString(
                                              localeString,
                                              timeDateFormat
                                          )
                                        : ""}
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
            <Loader active={loading} inline="centered" />
            <div className="parameters-pagination-container">
                <Pagination
                    activePage={page}
                    totalPages={Math.ceil(totalRows / NumberOfItemsToFetch)}
                    onPageChange={(_event, data) => setPage(data.activePage as number)}
                />
            </div>
        </>
    );
};
